import * as React from "react";
import {InputGroup, OverlayTrigger, Tooltip} from "react-bootstrap";
import {LcdIcon} from "../icon/LcdIcon";
import {Spinner} from "../spinner/Spinner";
import {defineMessages, InjectedIntlProps, injectIntl} from "react-intl";
import ReactDatePicker from "react-datepicker";
import {getLanguageAndLocale} from "../../intl/intl";
import {DateUtil} from "../../util/DateUtil";

const MESSAGES = defineMessages({
  NONE: {
    id: "studio.property.edit.none",
    defaultMessage: "None"
  }
});

interface DateEditProps {
  name?: string;
  value: Date;
  defaultValue?: Date;
  type?: string;
  onChange: (newValue: Date) => void | Promise<void>;
  onBlur?: React.FocusEventHandler<any>;
  readOnly?: boolean;
}

interface DateEditState {
  isMouseOver: boolean;
  isEditing: boolean;
  editingValue: Date;
  submitState: "NOT_STARTED" | "SUBMITTING" | "ERROR"; //no explicit FINISHED state, the edit field just goes back to 'display' mode
  submitError: Error;
  lastMouseOut: number;
}

/**
 * A Date edit field that can be edited upon mouseover.
 */
class DateEditComponent extends React.Component<DateEditProps & InjectedIntlProps, DateEditState> {

  constructor(props) {
    super(props);
    this.state = {
      isMouseOver: false,
      isEditing: false,
      editingValue: null,
      submitState: "NOT_STARTED",
      submitError: null,
      lastMouseOut: 0,
    };
  }

  reset = () => {
    this.setState({
      isEditing: false,
      editingValue: null,
      submitState: "NOT_STARTED",
      submitError: null,
    });
  }

  handleMouseOver = () => {
    // Remove this time hack when implementing a real solution for LF-2229
    if (Date.now() - this.state.lastMouseOut > 20) {
      this.setState({isMouseOver: true});
    }
  }

  handleMouseOut = () => {
    this.setState({isMouseOver: false, lastMouseOut: Date.now()});
  }

  handleChange = (newValue) => {
    this.setState(prevState => ({
      editingValue: newValue,
      submitState: prevState.submitState === "ERROR" ? "NOT_STARTED" : prevState.submitState
    }));
  }

  handleInputFocus = () => {
    this.setState(prevState => ({
      isEditing: true,
      editingValue: prevState.submitState === "ERROR" ? prevState.editingValue : this.props.value
    }));
  }

  handleInputBlur = (blurEvent) => {
    if (this.state.editingValue !== this.props.value && (this.state.submitState !== "ERROR")) {
      this.valueChanged(this.state.editingValue).then(this.reset).catch(this.reset);
    } else {
      this.reset();
    }
    if (this.props.onBlur) {
      this.props.onBlur(blurEvent);
    }
  }

  valueChanged = (newValue: Date): Promise<void> => {
    const onChangeResult = this.props.onChange(newValue);
    if (onChangeResult instanceof Promise) {
      this.setState({submitState: "SUBMITTING"});
      const onChangeResultPromise = onChangeResult as Promise<void>;
      return onChangeResultPromise.catch((error) => {
        this.setState({submitState: "ERROR", submitError: error});
        throw error;
      });
    } else {
      return Promise.resolve();
    }
  }

  shouldShowInput = () => {
    const {isMouseOver, isEditing} = this.state;
    return isMouseOver || isEditing;
  }

  escapeKeyPressedListener = (event: KeyboardEvent) => {
    //cancel on ESC, unfortunately ESC keys are only dispatched on document
    if (event.key === 'Escape') {
      //revert editingValue to this.props.value -> no onChange fired
      this.setState({editingValue: this.props.value});
    }
  }

  renderInput = () => {
    const {intl} = this.props;
    const {locale} = getLanguageAndLocale();
    if (this.shouldShowInput() && !this.props.readOnly) {
      const inputValue = this.state.isEditing ? this.state.editingValue : this.props.value;
      document.addEventListener("keyup", this.escapeKeyPressedListener);
      return <ReactDatePicker
          portalId="batchUpdate-datepicker"
          className={"batchUpdate"}
          onChange={this.handleChange}
          onCalendarClose={() => {
            this.handleInputBlur(null)
          }}
          onFocus={this.handleInputFocus}
          selected={typeof inputValue === "string" ? new Date(inputValue) : inputValue}
          showMonthDropdown={true}
          showYearDropdown={true}
          scrollableYearDropdown={true}
          showTimeSelect
          locale={locale}
          dateFormat={`${DateUtil.dateFormat()} ${DateUtil.timeFormat()}`}
          timeFormat={DateUtil.timeFormat()}
          timeIntervals={15}
      />;
    }
    document.removeEventListener("keyup", this.escapeKeyPressedListener);
    const formattedDate = this.props.value ? DateUtil.formatDateTime(this.props.value) : null;
    return (<div className="dateEditDisplay">
      {this.props.children || formattedDate || this.props.defaultValue || intl.formatMessage(MESSAGES.NONE)}
    </div>);
  }

  renderAddon = () => {
    if (this.props.readOnly) {
      return null;
    }
    const {submitState, submitError} = this.state;
    switch (submitState) {
    case "NOT_STARTED":
      return null;
    case "SUBMITTING":
      return <InputGroup.Addon><Spinner/></InputGroup.Addon>;
    case "ERROR": {
      const errorMessage = submitError.message;
      const tooltip = <Tooltip id={(this.props.name || "textedit") + "-error-tooltip"}>{errorMessage}</Tooltip>;
      return (
          <InputGroup.Addon>
            <OverlayTrigger placement="top" overlay={tooltip} defaultOverlayShown={true}>
              <LcdIcon icon="alert"/>
            </OverlayTrigger>
          </InputGroup.Addon>
      );
    }
    }
  }

  render() {
    return (
        <InputGroup className="dateEditContainer"
                    onMouseOver={this.handleMouseOver}
                    onMouseOut={this.handleMouseOut}>
          {this.renderInput()}{this.renderAddon()}
        </InputGroup>
    );
  }
}

export const DateEdit = injectIntl(DateEditComponent);